import { useState, useEffect } from 'react';
import { transfersApi, settingsApi } from '../api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeftRight, ArrowUpRight, ArrowDownLeft, Loader2, Info } from 'lucide-react';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function TransfersPage() {
  const { user, fetchUser } = useAuthStore();
  const [transfers, setTransfers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [transfersRes, settingsRes] = await Promise.all([transfersApi.getAll(), settingsApi.get()]);
      setTransfers(transfersRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateFee = () => {
    if (!amount || !settings) return 0;
    return parseFloat(amount) * (settings.transfer_fee_percent || 2) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recipient || !amount) { toast.error('Please fill in all required fields'); return; }
    
    const fee = calculateFee();
    const total = parseFloat(amount) + fee;
    if (total > (user?.balance || 0)) { toast.error('Insufficient balance'); return; }
    
    setSubmitting(true);
    try {
      await transfersApi.create({ recipient, amount: parseFloat(amount), note: note || undefined });
      toast.success('Transfer successful!');
      setRecipient(''); setAmount(''); setNote('');
      fetchData();
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Fund Transfers</h1>
        <div className="text-right">
          <p className="text-sm text-zinc-500">Current Balance</p>
          <p className="text-2xl font-bold text-[#FFD700]">{formatMoney(user?.balance || 0)}</p>
        </div>
      </div>

      <Tabs defaultValue="transfer">
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger value="transfer">Send Funds</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="transfer" className="space-y-6 mt-6">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader><CardTitle className="text-white">Transfer Funds</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Recipient (Email or Username)</Label>
                  <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Enter email or username" className="bg-[#121212] border-white/10 text-white" required data-testid="transfer-recipient-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount (₦)</Label>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" className="bg-[#121212] border-white/10 text-white" required min={settings?.min_transfer_amount || 100} max={settings?.max_transfer_amount || 1000000} data-testid="transfer-amount-input" />
                  <p className="text-xs text-zinc-500">Min: {formatMoney(settings?.min_transfer_amount || 100)} · Max: {formatMoney(settings?.max_transfer_amount || 1000000)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Note (Optional)</Label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" className="bg-[#121212] border-white/10 text-white" />
                </div>
                
                {amount && (
                  <Card className="bg-[#121212] border-white/10">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between"><span className="text-zinc-400">Amount</span><span className="text-white">{formatMoney(parseFloat(amount) || 0)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-400">Fee ({settings?.transfer_fee_percent || 2}%)</span><span className="text-white">{formatMoney(calculateFee())}</span></div>
                      <div className="border-t border-white/10 pt-2 flex justify-between font-bold"><span className="text-white">Total</span><span className="text-[#FFD700]">{formatMoney((parseFloat(amount) || 0) + calculateFee())}</span></div>
                    </CardContent>
                  </Card>
                )}

                <Button type="submit" disabled={submitting} className="w-full bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold" data-testid="submit-transfer-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</> : 'Send Funds'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {transfers.length === 0 ? (
            <div className="text-center py-12"><ArrowLeftRight className="w-16 h-16 mx-auto text-zinc-600 mb-4" /><p className="text-zinc-400">No transfer history</p></div>
          ) : (
            <div className="space-y-4">
              {transfers.map((transfer) => (
                <Card key={transfer.id} className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${transfer.type === 'sent' ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                        {transfer.type === 'sent' ? <ArrowUpRight className="w-5 h-5 text-red-500" /> : <ArrowDownLeft className="w-5 h-5 text-green-500" />}
                      </div>
                      <div>
                        <p className="font-semibold text-white">{transfer.type === 'sent' ? `To: ${transfer.recipient_username}` : `From: ${transfer.sender_username}`}</p>
                        <p className="text-sm text-zinc-500">{formatDate(transfer.created_at)}</p>
                        {transfer.note && <p className="text-sm text-zinc-400">{transfer.note}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${transfer.type === 'sent' ? 'text-red-500' : 'text-green-500'}`}>{transfer.type === 'sent' ? '-' : '+'}{formatMoney(transfer.amount)}</p>
                      {transfer.type === 'sent' && transfer.fee > 0 && <p className="text-xs text-zinc-500">Fee: {formatMoney(transfer.fee)}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
